/**
 * Pure filter + sort helpers for TimeEntry lists.
 *
 * The per-project Kronos tab can show 50+ entries once seeded; search
 * and sort are must-haves. Both helpers are dependency-free and
 * unit-tested — the view layer just passes them raw data + a couple
 * of lookup maps.
 */

import type { TimeEntry } from '@altyor-kronos/shared-types';

export interface EntryLookups {
  userLabelsById: ReadonlyMap<string, string>;
  designationLabelsById: ReadonlyMap<string, string>;
  /**
   * Resolved project label per projectId. Optional — the per-project
   * Kronos tab has a single project so sorting by it is a no-op, but
   * the tracker's today list spans multiple projects and benefits
   * from alphabetical grouping.
   */
  projectLabelsById?: ReadonlyMap<string, string>;
}

export type EntrySortKey =
  | 'startTime'
  | 'duration'
  | 'project'
  | 'designation'
  | 'teammate'
  | 'description';
export type EntrySortDir = 'asc' | 'desc';

/**
 * Normalize a string for accent- and case-insensitive substring
 * matching. `"Léo"` → `"leo"`, `"Révision"` → `"revision"`.
 *
 * The French team has names like *Léo Blanc* and *Carole Lefèvre* —
 * a naive `.toLowerCase().includes()` does NOT match `"leo"` against
 * `"léo"` because `é` and `e` are distinct code points. NFD splits
 * the accented char into (base, combining-mark), and the regex
 * strips the combining marks.
 *
 * `/\p{Diacritic}/gu` is Unicode-aware and covers all diacritics
 * (grave, acute, circumflex, cedilla, ring above, …) in one pass.
 */
export function foldText(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/**
 * Case-insensitive, **accent-insensitive** substring search across:
 * description, designation label, teammate display name, and tags.
 * Labels come from the lookup maps the tab already maintains for
 * rendering — no extra Firestore query.
 */
export function filterEntries(
  entries: readonly TimeEntry[],
  query: string,
  lookups: EntryLookups,
): TimeEntry[] {
  const needle = foldText(query.trim());
  if (needle === '') return [...entries];

  return entries.filter((e) => {
    const haystack: string[] = [];
    if (e.description) haystack.push(e.description);
    const desigLabel = lookups.designationLabelsById.get(e.designationId);
    if (desigLabel) haystack.push(desigLabel);
    const userLabel = lookups.userLabelsById.get(e.userId);
    if (userLabel) haystack.push(userLabel);
    if (e.tags && e.tags.length > 0) haystack.push(e.tags.join(' '));
    return haystack.some((s) => foldText(s).includes(needle));
  });
}

/**
 * Stable sort. Returns a fresh array — never mutates the input.
 *
 * - `startTime`, `duration`: numeric comparison
 * - `designation`, `teammate`: alphabetical via the lookup label;
 *   entries with no resolvable label sort last.
 *
 * Ties break on `startTime desc` so the visual ordering stays
 * predictable when multiple entries share the primary key.
 */
export function sortEntries(
  entries: readonly TimeEntry[],
  key: EntrySortKey,
  dir: EntrySortDir,
  lookups: EntryLookups,
): TimeEntry[] {
  const sign = dir === 'asc' ? 1 : -1;
  const copy = [...entries];

  copy.sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'startTime':
        cmp = a.startTime.toMillis() - b.startTime.toMillis();
        break;
      case 'duration':
        cmp = (a.durationSeconds ?? 0) - (b.durationSeconds ?? 0);
        break;
      case 'project': {
        const la =
          lookups.projectLabelsById?.get(a.projectId) ?? a.projectId ?? '\uffff';
        const lb =
          lookups.projectLabelsById?.get(b.projectId) ?? b.projectId ?? '\uffff';
        cmp = la.localeCompare(lb);
        break;
      }
      case 'designation': {
        const la = lookups.designationLabelsById.get(a.designationId) ?? '\uffff';
        const lb = lookups.designationLabelsById.get(b.designationId) ?? '\uffff';
        cmp = la.localeCompare(lb);
        break;
      }
      case 'teammate': {
        const la = lookups.userLabelsById.get(a.userId) ?? '\uffff';
        const lb = lookups.userLabelsById.get(b.userId) ?? '\uffff';
        cmp = la.localeCompare(lb);
        break;
      }
      case 'description': {
        // Empty descriptions sort last regardless of direction —
        // they carry no information for grouping.
        const la = a.description?.trim() ?? '';
        const lb = b.description?.trim() ?? '';
        if (la === '' && lb === '') {
          cmp = 0;
        } else if (la === '') {
          return 1;
        } else if (lb === '') {
          return -1;
        } else {
          cmp = la.localeCompare(lb);
        }
        break;
      }
    }
    if (cmp !== 0) return cmp * sign;
    // Tie-break: most recent first (mirrors the default view order).
    return b.startTime.toMillis() - a.startTime.toMillis();
  });

  return copy;
}

/**
 * Filter entries by the author's group (`departmentId`). The empty string
 * and the sentinel `'all'` both mean "no group filter" — return a copy of
 * everything. Otherwise keep only entries whose author's resolved group
 * equals `groupId`; an author missing from the map counts as the empty
 * group and is therefore excluded by any non-empty selection.
 *
 * Pure + dependency-free so the view layer just passes a `uid → departmentId`
 * lookup it already maintains for labels.
 */
export function filterEntriesByGroup(
  entries: readonly TimeEntry[],
  groupId: string,
  userDeptById: ReadonlyMap<string, string>,
): TimeEntry[] {
  if (groupId === '' || groupId === 'all') return [...entries];
  return entries.filter((e) => (userDeptById.get(e.userId) ?? '') === groupId);
}
